// Declaração de não associação: atestado, assinado só pela Associação, de que
// o consumidor reprovado na análise cadastral NÃO é associado.
//
// Mora em _shared/ porque dois lados precisam da mesma regra e do mesmo texto:
// a edge function `declaracao-nao-associacao` (Deno), que valida e gera o PDF,
// e o painel da negociação (DeclaracaoNaoAssociacaoPanel), que mostra o texto
// exato na confirmação e decide quem vê o botão. Mesmo padrão de emailDevolvido.ts.
//
// Por que existe: em 84 das 86 negociações reprovadas até out/2026 o cliente já
// tinha assinado o termo de adesão, e nada dizia a ele que esse termo não valeu.
// Design em docs/superpowers/specs/2026-10-16-declaracao-nao-associacao-design.md.

export const ASSOCIACAO = {
  razaoSocial: 'ASSOCIAÇÃO VIGOR ENERGY GO',
  cnpj: '57.072.689/0001-87',
  sede: 'Av. dos Trabalhadores, Quadra 01-B, Lote 08-A, Morrinhos/GO, CEP 75.650-000',
  cidade: 'Morrinhos/GO',
} as const;

export const TITULO_DECLARACAO = 'DECLARAÇÃO DE INEXISTÊNCIA DE VÍNCULO ASSOCIATIVO';

/** Papéis que emitem por conta própria. O vendedor responsável pela negociação também emite. */
export const PAPEIS_EMISSORES = ['adm', 'gerente', 'analista'] as const;

/** Situações de UC que significam "está na associação" — `Cancelada` e vazio não contam.
 * A função SQL `declaracao_nao_associacao_conflitos` usa a mesma lista: mexeu aqui, mexa lá. */
export const SITUACOES_UC_ASSOCIADA = ['Ativa', '1° Ciclo', '2° Ciclo', 'Ativa-S/-Rateio', 'Pausada'] as const;

export function podeEmitirDeclaracao(
  papel: string | null | undefined,
  usuarioId: string | null | undefined,
  responsavelId: string | null | undefined,
): boolean {
  if (papel && (PAPEIS_EMISSORES as readonly string[]).includes(papel)) return true;
  return !!usuarioId && !!responsavelId && usuarioId === responsavelId;
}

export interface ContextoEmissao {
  papel: string | null;
  usuarioId: string;
  responsavelId: string | null;
  statusNegociacao: string | null;
  decisaoFinal: string | null;
  clienteNome: string | null;
  cpfCnpj: string | null;
  /** Avalistas com status diferente de `removido`. */
  avalistasAtivos: number;
  /** Outras negociações do mesmo CPF/CNPJ em `termo_aprovado`. */
  outrasNegociacoesAprovadas: number;
  /** UCs desta negociação ou do mesmo CPF/CNPJ com situação de associada. */
  ucsAssociadas: number;
  /** UCs desta negociação que já têm fatura. */
  ucsComFatura: number;
}

/** Motivo pronto para o usuário quando a declaração NÃO pode sair, ou null quando pode.
 * A ordem importa: a primeira barreira encontrada é a que aparece. */
export function motivoRecusaEmissao(c: ContextoEmissao): string | null {
  if (!podeEmitirDeclaracao(c.papel, c.usuarioId, c.responsavelId)) {
    return 'Só adm, gerente, analista ou o vendedor responsável pela negociação podem emitir a declaração.';
  }
  if (c.statusNegociacao !== 'termo_reprovado') {
    return 'A declaração só pode ser emitida para negociação com termo reprovado.';
  }
  if (c.decisaoFinal !== 'Reprovado') {
    return 'A declaração só vale para reprovação de crédito. Esta negociação foi reprovada por outro motivo e ainda pode ser reanalisada.';
  }
  if (c.avalistasAtivos > 0) {
    return 'Há um avalista em andamento nesta negociação. Remova o avalista antes de emitir a declaração.';
  }
  if (!(c.clienteNome || '').trim()) {
    return 'A negociação está sem o nome do cliente. Corrija o cadastro antes de emitir.';
  }
  const doc = somenteDigitos(c.cpfCnpj);
  if (doc.length !== 11 && doc.length !== 14) {
    return 'A negociação está sem CPF/CNPJ válido. Corrija o cadastro antes de emitir.';
  }
  if (c.outrasNegociacoesAprovadas > 0 || c.ucsAssociadas > 0) {
    return 'Este CPF/CNPJ é associado por outra negociação ou UC. A declaração seria falsa.';
  }
  if (c.ucsComFatura > 0) {
    return 'Uma das UCs desta negociação já tem fatura emitida pela Vigor. A declaração seria falsa.';
  }
  return null;
}

export function somenteDigitos(v: string | null | undefined): string {
  return String(v || '').replace(/\D/g, '');
}

/** 000.000.000-00 ou 00.000.000/0000-00. Fora desses tamanhos, devolve o que veio. */
export function formatarCpfCnpj(v: string | null | undefined): string {
  const d = somenteDigitos(v);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return String(v || '').trim();
}

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/** Dia, mês e ano no fuso de Brasília — a emissão às 22h de Brasília já é "amanhã" em UTC. */
function partesBrasilia(data: Date): { dia: number; mes: number; ano: number } {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo', day: 'numeric', month: 'numeric', year: 'numeric',
  }).formatToParts(data);
  const valor = (tipo: string) => Number(partes.find(p => p.type === tipo)?.value);
  return { dia: valor('day'), mes: valor('month'), ano: valor('year') };
}

export function dataCurta(data: Date): string {
  const { dia, mes, ano } = partesBrasilia(data);
  return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
}

export function dataPorExtenso(data: Date): string {
  const { dia, mes, ano } = partesBrasilia(data);
  return `${dia} de ${MESES[mes - 1]} de ${ano}`;
}

/** "A", "A e B", "A, B e C". */
function listar(itens: string[]): string {
  if (itens.length <= 1) return itens.join('');
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** Trecho de um parágrafo; `negrito` vira Helvetica-Bold no PDF e <strong> na tela. */
export interface Trecho {
  texto: string;
  negrito?: boolean;
}

export interface DadosDeclaracao {
  clienteNome: string;
  cpfCnpj: string;
  ucs: string[];
  /** ISO de `negociacoes.data_assinatura_termo`; sem ela a frase omite a data. */
  termoAssinadoEm: string | null;
  procuradorNome: string;
  emitidaEm: Date;
}

export interface Declaracao {
  titulo: string;
  paragrafos: Trecho[][];
  localData: string;
  assinatura: { linha1: string; linha2: string };
}

export function montarDeclaracao(d: DadosDeclaracao): Declaracao {
  const doc = somenteDigitos(d.cpfCnpj);
  const pj = doc.length === 14;
  const qualificacao = pj
    ? `, inscrita no CNPJ sob o nº ${formatarCpfCnpj(doc)}, `
    : `, inscrito(a) no CPF sob o nº ${formatarCpfCnpj(doc)}, `;

  const ucs = d.ucs.map(u => u.trim()).filter(Boolean);
  const destinoUcs = ucs.length === 0
    ? ''
    : ucs.length === 1
      ? ` à unidade consumidora nº ${ucs[0]}`
      : ` às unidades consumidoras nº ${listar(ucs)}`;

  const termo = d.termoAssinadoEm ? new Date(d.termoAssinadoEm) : null;
  const emData = termo && !Number.isNaN(termo.getTime()) ? ` em ${dataCurta(termo)}` : '';

  const procurador = d.procuradorNome.trim();
  const cliente = d.clienteNome.trim().toUpperCase();

  return {
    titulo: TITULO_DECLARACAO,
    paragrafos: [
      [
        { texto: ASSOCIACAO.razaoSocial, negrito: true },
        { texto: `, inscrita no CNPJ sob o nº ${ASSOCIACAO.cnpj}, com sede na ${ASSOCIACAO.sede}, neste ato representada por seu procurador ` },
        { texto: procurador, negrito: true },
        { texto: ', DECLARA, para os devidos fins, que ' },
        { texto: cliente, negrito: true },
        { texto: qualificacao },
        { texto: 'não integra o quadro de associados', negrito: true },
        { texto: ' desta Associação.' },
      ],
      [
        {
          texto:
            `A solicitação de adesão apresentada à Associação${emData} não foi aceita e, portanto, não produziu efeitos. ` +
            `Em decorrência dela, não existe entre as partes vínculo associativo, obrigação financeira nem destinação ` +
            `de créditos de energia${destinoUcs}.`,
        },
      ],
    ],
    localData: `${ASSOCIACAO.cidade}, ${dataPorExtenso(d.emitidaEm)}.`,
    assinatura: { linha1: ASSOCIACAO.razaoSocial, linha2: `p.p. ${procurador} — Procurador` },
  };
}

/** Palavra pronta para desenhar: o espaço que a precede já entra na medida da linha. */
export interface Palavra {
  texto: string;
  negrito: boolean;
}

/** Quebra um parágrafo com trechos em negrito em linhas que cabem em `larguraMax`.
 *
 * O medidor é injetado (no PDF, `font.widthOfTextAtSize`) para a regra ser testável
 * sem pdf-lib. Uma palavra sozinha maior que a linha fica numa linha só, sem cortar. */
export function quebrarParagrafo(
  trechos: Trecho[],
  larguraMax: number,
  medir: (texto: string, negrito: boolean) => number,
): Palavra[][] {
  const palavras: Palavra[] = [];
  for (const t of trechos) {
    // Pontuação colada ao trecho anterior (", inscrita…") não pode virar palavra
    // nova com espaço antes — por isso o espaço é lido do próprio texto.
    const partes = t.texto.split(/(\s+)/);
    for (const parte of partes) {
      if (!parte) continue;
      if (/^\s+$/.test(parte)) {
        palavras.push({ texto: ' ', negrito: !!t.negrito });
      } else {
        palavras.push({ texto: parte, negrito: !!t.negrito });
      }
    }
  }

  // Junta pedaços sem espaço entre si ("Associação" + "." vira uma unidade só
  // para a quebra, mantendo o negrito de cada pedaço).
  const unidades: Palavra[][] = [];
  let atual: Palavra[] = [];
  for (const p of palavras) {
    if (p.texto === ' ') {
      if (atual.length) unidades.push(atual);
      atual = [];
    } else {
      atual.push(p);
    }
  }
  if (atual.length) unidades.push(atual);

  const largura = (u: Palavra[]) => u.reduce((s, p) => s + medir(p.texto, p.negrito), 0);
  const espaco = medir(' ', false);

  const linhas: Palavra[][] = [];
  let linha: Palavra[] = [];
  let larguraLinha = 0;
  for (const u of unidades) {
    const w = largura(u);
    if (linha.length && larguraLinha + espaco + w > larguraMax) {
      linhas.push(linha);
      linha = [];
      larguraLinha = 0;
    }
    if (linha.length) {
      linha.push({ texto: ' ', negrito: false });
      larguraLinha += espaco;
    }
    linha.push(...u);
    larguraLinha += w;
  }
  if (linha.length) linhas.push(linha);
  return linhas;
}

/** Texto corrido de um parágrafo — para a confirmação na tela e para os testes. */
export function textoDoParagrafo(trechos: Trecho[]): string {
  return trechos.map(t => t.texto).join('');
}
